import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface CollapsibleSectionProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  badge?: string | number;
  variant?: 'default' | 'premium' | 'compact';
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  subtitle,
  icon,
  children,
  defaultExpanded = true,
  className = '',
  headerClassName = '',
  contentClassName = '',
  badge,
  variant = 'default'
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const getVariantClasses = () => {
    switch (variant) {
      case 'premium':
        return {
          container: 'bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-sm border border-gray-600/50 rounded-xl shadow-xl',
          header: 'bg-gradient-to-r from-blue-400/10 to-purple-400/10 border-b border-gray-600/30',
          content: 'bg-gray-900/20'
        };
      case 'compact':
        return {
          container: 'bg-gray-800/40 border border-gray-700 rounded-lg',
          header: 'bg-gray-900/30',
          content: 'bg-transparent'
        };
      default:
        return {
          container: 'bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg',
          header: 'bg-gray-900/30 border-b border-gray-600',
          content: 'bg-transparent'
        };
    }
  };

  const variantClasses = getVariantClasses();

  return (
    <div className={`${variantClasses.container} transition-all duration-200 ${isExpanded ? 'ring-1 ring-blue-400/20' : ''} ${className}`}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-700/40 active:bg-gray-700/50 transition-all duration-150 ${variantClasses.header} ${headerClassName} group`}
      >
        <div className="flex items-center space-x-3 sm:space-x-4">
          {icon && (
            <div className="p-2 bg-blue-400/10 rounded-lg flex-shrink-0">
              {icon}
            </div>
          )}
          <div className="text-left">
            <div className="flex items-center space-x-3">
              <h3 className="text-base sm:text-lg font-semibold text-white">{title}</h3>
              {badge && (
                <span className="px-2 py-1 bg-blue-400/20 text-blue-400 text-xs rounded-full border border-blue-400/30">
                  {badge}
                </span>
              )}
            </div>
            {subtitle && (
              <p className="text-xs sm:text-sm text-gray-400 mt-1">{subtitle}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full transition-all duration-300 ${
            isExpanded ? 'bg-green-400 shadow-lg shadow-green-400/50' : 'bg-gray-500'
          }`}></div>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400 group-hover:text-gray-200 transition-all duration-150 transform group-hover:scale-110" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400 group-hover:text-gray-200 transition-all duration-150 transform group-hover:scale-110" />
          )}
        </div>
      </button>

      {/* Content */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out origin-top ${
          isExpanded ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0 h-0'
        }`}
        style={{
          transform: isExpanded ? 'scaleY(1)' : 'scaleY(0)',
          transformOrigin: 'top'
        }}
      >
        <div className={`p-4 sm:p-6 ${variantClasses.content} ${contentClassName}`}>
          {children}
        </div>
      </div>
    </div>
  );
};